/**
 * useResponsiveTask Hook
 *
 * A custom hook for responsive behavior in Task Management components.
 * Provides viewport information and breakpoint utilities.
 *
 * @example
 * const { isMobile, isTablet, isDesktop, breakpoint } = useResponsiveTask();
 *
 * if (isMobile) {
 *   return <MobileTaskView />;
 * }
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Breakpoint values matching common responsive design patterns
 */
const breakpoints = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

/**
 * Get the current breakpoint name based on window width
 * @returns {string} Current breakpoint name
 */
const getCurrentBreakpoint = () => {
  if (typeof window === 'undefined') return 'lg';
  const width = window.innerWidth;
  if (width < breakpoints.xs) return 'xs';
  if (width < breakpoints.sm) return 'sm';
  if (width < breakpoints.md) return 'md';
  if (width < breakpoints.lg) return 'lg';
  if (width < breakpoints.xl) return 'xl';
  return 'xxl';
};

/**
 * Check if current viewport is mobile (below md breakpoint)
 * @returns {boolean}
 */
const checkIsMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < breakpoints.md;
};

/**
 * Check if current viewport is desktop (at or above lg breakpoint)
 * @returns {boolean}
 */
const checkIsDesktop = () => {
  if (typeof window === 'undefined') return true;
  return window.innerWidth >= breakpoints.lg;
};

/**
 * Debounce utility for resize handler
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {Function} Debounced function
 */
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Custom hook for responsive Task Management behavior
 * @param {Object} options - Hook options
 * @param {number} options.debounceDelay - Resize debounce delay (default: 150ms)
 * @returns {Object} Responsive state and utilities
 */
export default function useResponsiveTask(options = {}) {
  const { debounceDelay = 150 } = options;

  // Initialize state with current values (SSR-safe)
  const [state, setState] = useState(() => ({
    breakpoint: typeof window !== 'undefined' ? getCurrentBreakpoint() : 'lg',
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
    isMobile: typeof window !== 'undefined' ? checkIsMobile() : false,
    isDesktop: typeof window !== 'undefined' ? checkIsDesktop() : true,
  }));

  // Update state on resize
  const updateState = useCallback(() => {
    const newState = {
      breakpoint: getCurrentBreakpoint(),
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: checkIsMobile(),
      isDesktop: checkIsDesktop(),
    };

    setState((prev) => {
      // Only update if values changed
      if (
        prev.breakpoint === newState.breakpoint &&
        prev.width === newState.width &&
        prev.height === newState.height
      ) {
        return prev;
      }
      return newState;
    });
  }, []);

  // Set up resize listener
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const debouncedUpdate = debounce(updateState, debounceDelay);

    window.addEventListener('resize', debouncedUpdate);

    // Initial update
    updateState();

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
    };
  }, [updateState, debounceDelay]);

  // Derived values
  const isTablet =
    state.width >= breakpoints.md && state.width < breakpoints.lg;
  const isExtraSmall = state.width < breakpoints.xs;

  // Layout helpers for Task Management
  const taskLayout = {
    // Board view: columns based on viewport
    boardColumns: state.isMobile ? 1 : state.isDesktop ? 4 : 2,

    // Form layout: vertical on mobile, horizontal on desktop
    formLayout: state.isMobile ? 'vertical' : 'horizontal',

    // Modal width: full on mobile, fixed on desktop
    modalWidth: state.isMobile ? '100%' : 720,

    // Drawer placement: bottom on mobile, right on desktop
    drawerPlacement: state.isMobile ? 'bottom' : 'right',

    // Table scroll: horizontal scroll on small screens
    tableScroll: state.width < breakpoints.lg ? { x: 800 } : undefined,

    // Pagination: simple on mobile, full on desktop
    paginationSimple: state.isMobile,

    // Card grid columns
    cardGridCols: state.isMobile ? 1 : state.isDesktop ? 3 : 2,
  };

  return {
    // Current breakpoint name
    breakpoint: state.breakpoint,

    // Viewport dimensions
    width: state.width,
    height: state.height,

    // Boolean flags for common breakpoints
    isMobile: state.isMobile,
    isTablet,
    isDesktop: state.isDesktop,
    isExtraSmall,

    // Task-specific layout helpers
    taskLayout,

    // Raw breakpoint values for custom logic
    breakpoints,

    // Utility to check if at or above a breakpoint
    isAtLeast: (bp) => state.width >= breakpoints[bp],

    // Utility to check if below a breakpoint
    isBelow: (bp) => state.width < breakpoints[bp],
  };
}
